import React, { type ComponentPropsWithoutRef } from "react";
import FileExportIcon from "shared/components/icons/file/FileExportIcon";
import PillarForm from "shared/components/pillar-form/PillarForm";
import {
  buildExportCSVText,
  CSV_CONTENT_TYPE,
} from "shared/components/pillar-table/export/build-export-csv-text";
import {
  buildExportColumnsFromCustomReport,
  buildExportSheets,
  type ExportColumns,
} from "shared/components/pillar-table/export/build-export-sheets";
import {
  buildExportXLSXBuffer,
  XLSX_CONTENT_TYPE,
} from "shared/components/pillar-table/export/build-export-xlsx-buffer";
import { browserExportHtmlToText } from "shared/components/pillar-table/export/export-html-to-text";
import { isReachedLimitExcelRows } from "shared/components/pillar-table/export/isReachedLimitExcelRows";
import { type PillarTableExportToExcelColumnProps } from "shared/components/pillar-table/export/PillarTableExportToExcelColumn";
import usePillarTableExportFileSizeValues from "shared/components/pillar-table/export/usePillarTableExportFileSizeValues";
import {
  usePillarTableContext,
  type DataQueryFunction,
  type PillarTableContextType,
} from "shared/components/pillar-table/PillarTableContext";
import {
  type CustomSessionType,
  useCustomSession,
} from "shared/context/CustomSessionContext";
import { type FiltersRequest } from "shared/filter-where-clause";
import { saveBinaryBufferToFile } from "shared/helpers/file/saveBinaryBufferToFile";

export type PillarTableExportToExcelProps<T> = Omit<
  ComponentPropsWithoutRef<"button">,
  "onClick"
> & {
  worksheetName: string;
  children:
    | Array<React.ReactElement<PillarTableExportToExcelColumnProps<T>>>
    | React.ReactElement<PillarTableExportToExcelColumnProps<T>>;
};

const PillarTableExportToExcel = <T,>({
  worksheetName,
  children,
  ...props
}: PillarTableExportToExcelProps<T>) => {
  const tableContext = usePillarTableContext<T, unknown>();
  const session = useCustomSession();
  const {
    dataQuery,
    tableRowResults,
    totalResults,
    filtersRequest,
    customReportResponse,
    testidTable,
  } = tableContext;

  if (customReportResponse?.exportColumns?.length) {
    children = buildExportColumnsFromCustomReport(
      customReportResponse.exportColumns,
    );
  }

  const { estimatedFileSizeKb } = usePillarTableExportFileSizeValues();

  const handleExport = async () => {
    if (!filtersRequest) {
      throw new Error("filters is required");
    }

    if (dataQuery instanceof Function) {
      await downloadThenProcessAndExportData<T>({
        dataQuery,
        filtersRequest,
        totalResults: totalResults!,
        estimatedFileSizeKb,
        children,
        worksheetName,
        tableContext,
        session,
      });
    } else {
      processAndExportData({
        combinedData: tableRowResults,
        children,
        worksheetName,
        tableContext,
        session,
      });
    }
  };
  if (!children || (Array.isArray(children) && children.length === 0)) {
    return <></>;
  }

  return (
    <PillarForm.LoadingButton
      testid={testidTable ? `${testidTable}-export-to-excel-button` : undefined}
      onClick={() => handleExport()}
      title="Export"
      label="Export"
      className="w-12"
      icon={<FileExportIcon />}
      {...props}
    />
  );
};

export default PillarTableExportToExcel;

const downloadThenProcessAndExportData = async <T,>({
  dataQuery,
  filtersRequest,
  totalResults,
  estimatedFileSizeKb,
  children,
  worksheetName,
  tableContext,
  session,
}: {
  dataQuery: DataQueryFunction<T, unknown>;
  filtersRequest: FiltersRequest;
  totalResults: number;
  estimatedFileSizeKb: number;
  children:
    | Array<React.ReactElement<PillarTableExportToExcelColumnProps<T>>>
    | React.ReactElement<PillarTableExportToExcelColumnProps<T>>;
  worksheetName: string;
  tableContext: PillarTableContextType<T, unknown>;
  session: CustomSessionType;
}) => {
  if (estimatedFileSizeKb <= 3500) {
    const data = await dataQuery(filtersRequest, {
      page: 1,
      pageSize: totalResults,
    });
    processAndExportData({
      combinedData: data.results,
      children,
      worksheetName,
      tableContext,
      session,
    });
  } else {
    const numberOfChunks = Math.ceil(estimatedFileSizeKb / 3500);
    const pageSize = Math.ceil(totalResults / numberOfChunks);

    // Create an array of page indexes [1, 2, 3, ..., numberOfChunks]
    const chunkIndexes = Array.from(
      { length: numberOfChunks },
      (_, i) => i + 1,
    );

    const allData: T[] = [];

    // Process up to 10 chunks at a time in parallel
    const concurrency = 10;
    for (let i = 0; i < chunkIndexes.length; i += concurrency) {
      // Slice out the next up-to-10 pages
      const slice = chunkIndexes.slice(i, i + concurrency);

      // Build an array of promises for this batch
      const dataPromises = slice.map((pageIndex) =>
        dataQuery(filtersRequest, { page: pageIndex, pageSize }),
      );

      // Wait for the batch to finish
      const batchResponses = await Promise.all(dataPromises);
      // Flatten the batch results into allData
      for (const response of batchResponses) {
        allData.push(...response.results);
      }
    }

    processAndExportData({
      combinedData: allData,
      children,
      worksheetName,
      tableContext,
      session,
    });
  }
};

const processAndExportData = <T,>({
  combinedData,
  children,
  worksheetName,
  tableContext,
  session,
}: {
  combinedData: T[];
  children: ExportColumns<T>;
  worksheetName: string;
  tableContext: PillarTableContextType<T, unknown>;
  session: CustomSessionType;
}) => {
  const sheets = buildExportSheets<T>({
    rows: combinedData,
    columns: children,
    worksheetName,
    tableContext,
    session,
    htmlToText: browserExportHtmlToText,
  });

  //if mainTableWsData is more than 34,000 rows we want a csv file otherwise we want an xlsx file
  if (!isReachedLimitExcelRows(sheets.mainSheet.data.length)) {
    saveBinaryBufferToFile(
      buildExportXLSXBuffer({ sheets }),
      `${worksheetName}.xlsx`,
      XLSX_CONTENT_TYPE,
    );
    return;
  }

  saveCSVFile(sheets.mainSheet.data, worksheetName);
  sheets.nestedSheets.forEach((nestedSheet) => {
    saveCSVFile(nestedSheet.data, `${worksheetName} - ${nestedSheet.name}`);
  });
};

const saveCSVFile = (wsData: unknown[][], worksheetName: string) => {
  const uintArray = new TextEncoder().encode(
    buildExportCSVText({ data: wsData }),
  );
  saveBinaryBufferToFile(uintArray, `${worksheetName}.csv`, CSV_CONTENT_TYPE);
};
